import React from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import isObject from 'lodash/isObject';
import { flexRender } from '@tanstack/react-table';

// LOCAL VARIABLES
import { ROW_HEIGHT } from '../../variables';

// GLOBAL COMPONENTS
import Sprite from 'core/tools/Sprite';

// LOCAL COMPONENTS
import TableHeaderTooltip from './TableHeaderTooltip';
import DollarsCell from '../../tools/DollarsCell';
import NumberCell from '../../tools/NumberCell';

// STYLES
import styles from './tableHeader.module.scss';

export default function TableHeader({ className, groups }) {
  return (
    <thead
      className={clsx('thead table-header position-sticky', styles.head, className)}
      style={{ top: 0 }}
    >
      {groups.map(group => (
        <tr
          key={group.id}
          className={clsx('tr table-header-row d-flex w-100 p-0', styles.row)}
        >
          {group.headers.map(header => {
            const width = header.getSize();
            const canSort = header.column.getCanSort();
            const canResize = header.column.getCanResize();
            const sortOrder = header.column.getIsSorted();
            const isSortedDesc = sortOrder === 'desc';
            const isSorted = !!sortOrder;
            const isResizing = header.column.getIsResizing();

            const {
              cell: Cell,
              className,
              codes,
              headerClassName,
              icon,
              id,
              tooltip,
            } = header.column.columnDef || {};

            return (
              <th
                key={header.id}
                className={clsx(
                  'th d-flex align-items-center table-header-col',
                  !!codes && 'justify-content-center',
                  (Cell === DollarsCell || Cell === NumberCell) && 'justify-content-end',
                  styles.th,
                  className,
                  headerClassName,
                  canSort && isSorted && 'bg-primary text-white',
                )}
                style={{
                  flexGrow: id !== 'actions' && width,
                  flexBasis: width,
                  width,
                  minHeight: ROW_HEIGHT,
                  cursor: canSort ? 'pointer' : 'auto',
                }}
                onClick={header.column.getToggleSortingHandler()}
              >
                <TableHeaderTooltip columnKey={id} tooltip={tooltip} codes={codes}>
                  <div className={clsx(styles.sort, 'd-flex align-items-center table-header-cell')}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {icon && <Sprite {...(isObject(icon) ? icon : { use: icon })} />}
                    {canSort && (
                      <div className={clsx(
                        styles.sortIcon,
                        canSort && isSorted && styles.active,
                        isSortedDesc ? styles.desc : styles.asc
                      )}>
                        <Sprite
                          use="arrow-sortup"
                          size="xs"
                          className={styles.sortAsc}
                        />
                        <Sprite
                          use="arrow-sortdown"
                          size="xs"
                          className={styles.sortDesc}
                        />
                      </div>
                    )}
                  </div>
                </TableHeaderTooltip>
                <div
                  className={clsx(canResize && styles.resizer, isResizing && 'resizing')}
                  onDoubleClick={() => header.column.resetSize()}
                  onMouseDown={header.getResizeHandler()}
                  onTouchStart={header.getResizeHandler()}
                  onClick={e => e.stopPropagation()}
                />
              </th>
            )
          })}
        </tr>
      ))}
    </thead>
  );
}
